import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { json, useParams, useSearchParams } from "@remix-run/react";
import { dehydrate, QueryClient, useQuery } from "@tanstack/react-query";
import { CategoryItem, WorkspaceSearchResult } from "~/.server/api/routers/workspace";
import { trpcServer } from "~/common/trcp";
import { useCreatePageUrl } from "~/utils/hooks/useCreatePageUrl";
import WorkspaceListLayout from "~/components/workspaceListLayout";
import WorkspaceList from "~/components/workspaceList";
import type{ SortBy, SortOrder, SortValue } from "~/components/sorting";
import { deserializeBounds } from "~/utils/mapCache";
import { useCallback } from "react";

const PAGINATION_PAGE_SIZE = 100

// Map category slugs to display labels
const CATEGORY_LABELS: Record<string, string> = {
  'oficina': 'Oficinas',
  'coworking': 'Coworking',
  'taller': 'Talleres',
  'local': 'Locales',
  'sala-de-reuniones': 'Salas de reuniones',
  'estudio': 'Estudios',
  'almacen': 'Almacenes',
  'alquiler-consulta-medica': 'Consultas médicas',
  'ciudad': 'Espacios',
};

export async function loader({ request, params }: LoaderFunctionArgs) {
  const { category, city } = params;
  const url = new URL(request.url);
  const page = parseInt(url.searchParams.get('page') || '1', 10);
  const [sortBy, sortOrder] = (url.searchParams.get('sort') || 'createdAt-desc').split('-') as [SortBy, SortOrder];
  const boundsParam = url.searchParams.get('bounds');
  const bounds = deserializeBounds(boundsParam);

  const queryClient = new QueryClient()
  const isFilteredByCity = city !== 'all'
  const pageSize = isFilteredByCity ? PAGINATION_PAGE_SIZE : 20;

  await queryClient.prefetchQuery({
    queryKey: [category, city, page, sortBy, sortOrder, boundsParam],
    queryFn: async () => await trpcServer(request).workspace.getFilteredWorkspaces.query({
      pagination: { page, pageSize },
      sorting: { sortBy, sortOrder },
      filters: {
        ...(category !== "ciudad" ? { category } : {}),
        ...(city !== 'all' ? { citySlug: city } : {}),
        ...(bounds ? { bounds } : {})
      }
    }),
  });

  const totalCount = queryClient.getQueryData<WorkspaceSearchResult>([category, city, page, sortBy, sortOrder, boundsParam])?.pagination?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);
  const categoryLabel = category ? (CATEGORY_LABELS[category] || category.charAt(0).toUpperCase() + category.slice(1).replace(/-/g, ' ')) : '';

  return json({
    dehydratedState: dehydrate(queryClient),
    page,
    totalPages,
    city,
    category,
    categoryLabel,
  })
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const city = data?.city;
  const category = data?.category;
  const categoryLabel = data?.categoryLabel || '';
  const page = data?.page || 1;
  const totalPages = data?.totalPages || 1;
  const isFilteredByCity = city !== 'all';

  const formattedCity = isFilteredByCity && city
    ? city.charAt(0).toUpperCase() + city.slice(1)
    : 'todas las ciudades';

  const titleCity = isFilteredByCity ? ` en ${formattedCity}` : '';
  const title = `${categoryLabel}${titleCity}${page > 1 ? ` - Página ${page}` : ''} | Compartir Espacios`;
  const description = `Encuentra espacios de ${categoryLabel.toLowerCase()}${isFilteredByCity ? ` en ${formattedCity}` : ''}. Alquila el espacio perfecto para tu negocio.`;
  const canonicalUrl = `https://www.compartirespacios.com/espacios/${city}/${category}`;
  const pageUrl = page > 1 ? `${canonicalUrl}?page=${page}` : canonicalUrl;

  return [
    { title },
    { name: "description", content: description },
    { property: "og:url", content: pageUrl },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:locale", content: "es_ES" },
    { property: "og:site_name", content: "Compartir Espacios" },
    { name: "twitter:card", content: "summary" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { tagName: "link", rel: "canonical", href: page > 1 ? pageUrl : canonicalUrl },
    ...(page > 1 ? [{ tagName: "link", rel: "prev", href: page === 2 ? canonicalUrl : `${canonicalUrl}?page=${page - 1}` }] : []),
    ...(page < totalPages ? [{ tagName: "link", rel: "next", href: `${canonicalUrl}?page=${page + 1}` }] : []),
  ];
};

export default function Category() {
  const { category, city } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') || '1', 10);
  const sort = (searchParams.get('sort') || 'createdAt-desc') as SortValue;
  const boundsParam = searchParams.get('bounds');
  const createPageUrl = useCreatePageUrl();
 
  const handleSort = (value: SortValue) => {
    const params = new URLSearchParams(searchParams);
    params.set('sort', value);
    params.set('page', '1');
    setSearchParams(params);
  };
 
  const [sortBy, sortOrder] = sort.split('-') as [SortBy, SortOrder];
 
  const { data } = useQuery<WorkspaceSearchResult>({
    queryKey: [category, city, page, sortBy, sortOrder, boundsParam]
  });

  // This callback is required by the WorkspaceListLayout component
  // The actual work happens in the map component which updates URL params
  const handleBoundsChange = useCallback(() => {
    // The URL params are already updated in the map component
    // which triggers a refetch via the queryKey dependency
  }, []);
 
  const { data: categories } = useQuery<CategoryItem[]>({ queryKey: ['categories'] });
  const isFilteredByCity = city !== 'all'
  const categoryItem = categories?.find(cat => cat.value === category);
  const formattedCategoryName = categoryItem?.label || 
  (category ? category.charAt(0).toUpperCase() + category.slice(1) : '');
  const formattedCityName = (isFilteredByCity && city) 
  ? city.charAt(0).toUpperCase() + city.slice(1)
  : 'todas las ciudades' 

  return (
    <div className="container mx-auto px-4">

        <WorkspaceListLayout
          title={`Espacios de ${formattedCategoryName}${isFilteredByCity ? ` en ${formattedCityName}` : ''}`}
          showMap={isFilteredByCity}
          workspaces={data?.workspaces || []}
          totalCount={data?.pagination?.totalCount}
          searchWithinBounds={true}
          onBoundsChange={handleBoundsChange}
          sort={{
            value: sort,
            onValueChange: handleSort
          }}
        >
          <WorkspaceList
            data={data}
            showMap={isFilteredByCity}
            createPageUrl={createPageUrl}
            pageSize={PAGINATION_PAGE_SIZE}
          />
        </WorkspaceListLayout>
    </div>
  )
}
